import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import {
  Storefront,
  ShoppingCart,
  Users,
  Bell,
  Gear,
  TrendUp,
  House,
  InstagramLogo,
  Binoculars,
  Shield,
  SignOut,
  X,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * Responsive sidebar.
 * - Desktop (md+):  always visible, w-64, fixed-left.
 * - Mobile  (<md):  off-canvas drawer. Layout owns the open/close state
 *   and passes `mobileOpen` + `onClose`. We render a backdrop ONLY on
 *   mobile and slide the panel in/out with translate-x.
 */
export const Sidebar = ({ currentPath, user, onLogout, mobileOpen = false, onClose }) => {
  const [watchlistBadge, setWatchlistBadge] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const fetchBadge = async () => {
      if (document.hidden) return;  // don't poll for hidden tabs — WS + mouse/tab-focus refresh covers reactivity
      try {
        const res = await api.getWatchlistUnviewedMatches();
        setWatchlistBadge(res.data.unviewed_count || 0);
      } catch {}
    };
    fetchBadge();
    // Bumped from 15s → 45s. WebSocket pushes `watchlist_match` events in
    // real-time via handleRealtime below, so this interval is only a fallback
    // for when the socket is down.
    const interval = setInterval(fetchBadge, 45000);

    // Real-time: refresh badge immediately when a watchlist_match WS event arrives
    const handleRealtime = (e) => {
      if (e?.detail?.notification_type === "watchlist_match") {
        fetchBadge();
      }
    };
    window.addEventListener("id:notification", handleRealtime);

    return () => {
      clearInterval(interval);
      window.removeEventListener("id:notification", handleRealtime);
    };
  }, []);

  // Clear badge when user navigates to watchlist
  useEffect(() => {
    if (location.pathname === "/watchlist" && watchlistBadge > 0) {
      api.markWatchlistViewed().then(() => setWatchlistBadge(0)).catch(() => {});
    }
  }, [location.pathname, watchlistBadge]);

  // Close the drawer automatically on every route change so users don't
  // have to tap X after picking a nav item. Watch ONLY pathname — not
  // onClose — because Layout recreates onClose on every render, which
  // would otherwise re-fire this effect on every parent render and
  // immediately close the drawer the user just opened.
  const firstNavRef = useRef(true);
  useEffect(() => {
    if (firstNavRef.current) {
      firstNavRef.current = false;
      return;
    }
    if (onClose) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const navItems = [
    { path: "/", icon: House, label: "Dashboard" },
    { path: "/orders", icon: ShoppingCart, label: "Orders" },
    { path: "/influencers", icon: Users, label: "Customer Influencers" },
    { path: "/potential-influencers", icon: TrendUp, label: "Subscriber Influencers" },
    { path: "/social-followers", icon: InstagramLogo, label: "Social Media Followers" },
    { path: "/name-lookup", icon: MagnifyingGlass, label: "Name Lookup" },
    { path: "/watchlist", icon: Binoculars, label: "Watchlist", badge: watchlistBadge },
    { path: "/notifications", icon: Bell, label: "Notifications" },
    { path: "/settings", icon: Gear, label: "Settings" },
  ];

  if (user?.role === "admin") {
    navItems.push({ path: "/admin", icon: Shield, label: "Admin Panel" });
  }

  return (
    <>
      {/* Mobile-only backdrop. Hidden by `pointer-events-none` when closed
          so it never traps taps on desktop. */}
      <div
        className={`md:hidden fixed inset-0 bg-black/60 z-40 transition-opacity duration-200 ${
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
        data-testid="sidebar-backdrop"
      />

      <aside
        className={`w-64 bg-[#0A0A0A] min-h-screen fixed left-0 top-0 z-50 flex flex-col transition-transform duration-200 ease-out
          ${mobileOpen
            ? "translate-x-0 pointer-events-auto"
            : "-translate-x-full md:translate-x-0 pointer-events-none md:pointer-events-auto"}`}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#9FFF00] flex items-center justify-center">
              <span className="text-black font-black text-sm">ID</span>
            </div>
            <div>
              <h1 className="text-lg font-black tracking-tight text-white font-heading">INFLUENCE<span className="text-[#9FFF00]">DETECTOR</span></h1>
            </div>
          </Link>

          {/* Close button — mobile-only */}
          <button
            type="button"
            onClick={onClose}
            className="md:hidden p-1 -mr-1 text-white/60 hover:text-white"
            aria-label="Close menu"
            data-testid="sidebar-close"
          >
            <X size={22} weight="bold" />
          </button>
        </div>

        <nav className="p-4 flex-1 overflow-y-auto">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center gap-3 px-4 py-3 mb-1 text-sm font-medium transition-colors relative ${
                currentPath === item.path
                  ? "bg-[#9FFF00] text-black font-bold"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <item.icon size={20} weight={currentPath === item.path ? "fill" : "regular"} />
              {item.label}
              {item.badge > 0 && (
                <span className="absolute right-3 flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="watchlist-badge">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <Storefront size={20} weight="regular" className="text-white/40" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.company_name || "My Store"}</p>
              <p className="text-xs text-white/40 capitalize truncate">
                {user?.role === "admin" ? "Admin" : (user?.plan ? `${user.plan} plan` : "")}
              </p>
            </div>
          </div>

          {/* Credits counter — surfaced here because users instinctively
              click their profile / company name to check account-level
              info. Reads from `user.credits` (the snapshot enriched by
              /auth/me) and falls back to the flat `credits_*` fields
              that signup writes — so brand-new users see "0 / 0" with
              "Upgrade" hint instead of a missing counter.
              Admins have no credit limit, so we hide the counter
              entirely for them — otherwise it shows a confusing
              "2,000 / 2,000" and crowds out the Sign Out button. */}
          {user?.role !== "admin" && (() => {
            const c = user?.credits;
            const limitFlat = (user?.credits_limit ?? 0) + (user?.credit_bonus ?? 0);
            const usedFlat = user?.credits_used ?? 0;
            const remainingFlat = Math.max(0, (user?.credits_limit ?? 0) - usedFlat) + (user?.credit_bonus ?? 0);
            const remaining = c
              ? Math.max(0, (c.limit ?? 0) - (c.used ?? 0)) + (c.bonus ?? 0)
              : remainingFlat;
            const limit = c ? (c.limit ?? 0) + (c.bonus ?? 0) : limitFlat;
            const cycleEnd = c?.cycle_ends_at || user?.cycle_ends_at;
            const pct = limit > 0 ? Math.min(100, Math.round((remaining / limit) * 100)) : 0;
            const isLow = limit > 0 && pct <= 20;
            const planLabel = (user?.plan || "").toString();
            const isTrialPending = planLabel === "trial_pending" || limit === 0;
            return (
              <Link
                to="/billing"
                className="block px-4 py-3 mb-2 bg-white/5 hover:bg-white/10 transition-colors border border-white/5"
                data-testid="sidebar-credits-counter"
              >
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/45">
                    Credits this month
                  </span>
                  {isTrialPending ? (
                    <span className="text-xs font-bold text-white/60">
                      No plan yet
                    </span>
                  ) : (
                    <span className={`text-xs font-bold tabular-nums ${isLow ? "text-amber-300" : "text-[#9FFF00]"}`}>
                      {remaining.toLocaleString()}
                      <span className="text-white/35 font-normal"> / {limit.toLocaleString()}</span>
                    </span>
                  )}
                </div>
                {isTrialPending ? (
                  <p className="text-[10px] text-white/45 mt-0.5">
                    Add a card to unlock credits & detection.
                  </p>
                ) : (
                  <>
                    <div className="h-1 bg-white/10 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${isLow ? "bg-amber-300" : "bg-[#9FFF00]"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {cycleEnd && (
                      <p className="text-[10px] text-white/35 mt-1.5">
                        Resets {new Date(cycleEnd).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </p>
                    )}
                  </>
                )}
              </Link>
            );
          })()}

          <button
            onClick={onLogout}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-white/40 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="logout-button"
          >
            <SignOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
};
